/**
 * Admin Query Hooks
 *
 * React Query hooks for the admin user-management screen.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './keys';
import { adminService, type AdminUser, type SetFreePassInput } from '@/services/admin';

/** List/search users. Pass a (possibly empty) search string. */
export function useAdminUsers(search: string) {
  return useQuery({
    queryKey: queryKeys.admin.users(search),
    queryFn: () => adminService.listUsers(search),
    staleTime: 30 * 1000,
  });
}

/** Grant or revoke a user's free pass, then refresh the user lists. */
export function useSetFreePass() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: SetFreePassInput }) =>
      adminService.setFreePass(userId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
    },
  });
}

export type { AdminUser };
